// API Configuration for Voyabay Web App
// All LiteAPI calls go through Cloudflare Worker proxy (production key is on worker)
import { auth } from '../firebase'
import { formatDateOnly, flightDateKey } from '../utils/flightDateTime'

export const CLOUDFLARE_WORKER_URL = 'https://voyabayliteapi.dylan-mclean.workers.dev';

// Alias for backward compatibility
export const LITEAPI_PROXY_URL = CLOUDFLARE_WORKER_URL;

// Auth token for Cloudflare Worker
export const VOYABAY_API_TOKEN = 'vby_sk_a8f3k2m9x7p4q1w6e5r0t3y8u2i9o6';

// ---------------------------------------------------------------------------
// FLIGHTS
// ---------------------------------------------------------------------------
// Flights use a dedicated Cloudflare Worker so LiteAPI keys stay server-side.
// Set VITE_FLIGHTS_WORKER_URL only when pointing a local build at a preview
// worker. The app no longer reads VITE_LITEAPI_SANDBOX_KEY.
// ---------------------------------------------------------------------------
export const FLIGHTS_WORKER_URL = import.meta.env.VITE_FLIGHTS_WORKER_URL || 'https://voyabay-flights.dylan-mclean.workers.dev';
export const FLIGHTS_BASE_URL = FLIGHTS_WORKER_URL;

// Fetch wrapper for all flights API calls.
export const fetchFlightsAPI = (url, options = {}) =>
  fetchWithAuth(url, options);
// Search flight rates — POST /flights/rates
// Accepts simple page-level params and translates to LiteAPI's legs[] schema.
// params: { origin, destination, departureDate, returnDate?, adults, childAges, infants, cabinClass, currency }
const normalizeFlightRequestDate = (value) => {
  if (!value) return ''
  return flightDateKey(value) || formatDateOnly(value)
}

export const searchFlightRates = async (params) => {
  const url = `${FLIGHTS_BASE_URL}/flights/rates`

  const legs = [{
    origin: params.origin,
    destination: params.destination,
    date: normalizeFlightRequestDate(params.departureDate),
    direction: 'OUTBOUND',
  }]
  if (params.returnDate) {
    legs.push({
      origin: params.destination,
      destination: params.origin,
      date: normalizeFlightRequestDate(params.returnDate),
      direction: 'INBOUND',
    })
  }

  const adults = Math.max(1, Number(params.adults) || 1)
  const requestedChildren = Math.max(0, Number(params.children) || 0)
  const childAges = (Array.isArray(params.childAges) ? params.childAges : [])
    .map(age => Number(age))
    .filter(age => Number.isFinite(age) && age >= 2 && age <= 11)
  const normalizedChildAges = childAges.length >= requestedChildren
    ? childAges
    : [...childAges, ...Array.from({ length: requestedChildren - childAges.length }, () => 8)]
  const infants = Math.max(0, Math.min(Number(params.infants) || 0, adults))

  const body = {
    legs,
    adults,
    children: normalizedChildAges.length,
    childrenAges: normalizedChildAges,
    infants,
    cabinClass: (params.cabinClass || 'economy').toUpperCase(),
    currency: params.currency || 'USD',
  }

  const resp = await fetchFlightsAPI(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  const text = await resp.text()
  let json
  try { json = JSON.parse(text) } catch { json = { raw: text } }
  if (!resp.ok) {
    const err = new Error(`Flight rates failed: ${resp.status}`)
    err.status = resp.status
    err.body = json
    err.requestBody = body
    throw err
  }
  return json
}

// Helper: parse JSON or wrap a non-JSON response so callers can always inspect the body
const parseFlightResponse = async (resp, errorPrefix) => {
  const text = await resp.text()
  let json
  try { json = JSON.parse(text) } catch { json = { raw: text } }
  if (!resp.ok) {
    const err = new Error(`${errorPrefix}: ${resp.status}`)
    err.status = resp.status
    err.body = json
    throw err
  }
  return json
}

// Retrieve a single flight booking by its bookingId — live status + airline PNRs.
// GET {FLIGHTS_BASE_URL}/flights/bookings/{bookingId}
// Response shape: { data: [ { booking: {...} } ] }
export const getFlightBooking = async (bookingId) => {
  if (!bookingId) throw new Error('bookingId required')
  const url = `${FLIGHTS_BASE_URL}/flights/bookings/${encodeURIComponent(bookingId)}`
  const resp = await fetchFlightsAPI(url)
  const json = await parseFlightResponse(resp, 'Flight booking fetch failed')
  return json?.data?.[0]?.booking || json?.booking || json?.data || json
}

// Phase 1: create prebook — locks in fare, returns Stripe transactionId/secretKey + ancillary options
// body: { offerId, usePaymentSdk: true, contact: {...}, passengers: [...] }
export const flightPrebook = async (body) => {
  const url = `${FLIGHTS_BASE_URL}/flights/prebooks`
  const resp = await fetchFlightsAPI(url, { method: 'POST', body: JSON.stringify(body) })
  return parseFlightResponse(resp, 'Flight prebook failed')
}

// Phase 2: attach selected services (seats / bags / etc.) to an existing prebook
// Returns updated prebook with new transactionId/secretKey reflecting the new total
export const flightAttachServices = async (prebookId, selectedServices) => {
  const url = `${FLIGHTS_BASE_URL}/flights/prebooks/${encodeURIComponent(prebookId)}/services`
  const resp = await fetchFlightsAPI(url, {
    method: 'POST',
    body: JSON.stringify({ selectedServices }),
  })
  return parseFlightResponse(resp, 'Attach services failed')
}

// Phase 3: confirm booking after Stripe payment intent succeeds
// payment.method = 'TRANSACTION_ID' uses the Stripe transactionId; 'CREDIT' uses credit line
export const flightBook = async ({ prebookId, transactionId }) => {
  const url = `${FLIGHTS_BASE_URL}/flights/bookings`
  const resp = await fetchFlightsAPI(url, {
    method: 'POST',
    body: JSON.stringify({
      prebookId,
      payment: { method: 'TRANSACTION_ID', transactionId },
    }),
  })
  return parseFlightResponse(resp, 'Flight booking failed')
}

// Search airports by query string — returns flat array of airport objects
// Each airport: { iata, icao?, name, city, country, state?, lat, lon, tz }
export const searchAirports = async (query, signal) => {
  if (!query || query.length < 2) return [];
  const url = `${FLIGHTS_BASE_URL}/data/flights/airports?q=${encodeURIComponent(query)}`;
  const resp = await fetchFlightsAPI(url, signal ? { signal } : {});
  if (!resp.ok) throw new Error(`Airport search failed: ${resp.status}`);

  const json = await resp.json();
  // API returns data[].airports[] grouped by city — flatten to a single list.
  // Deduplicate by iata+icao since "All Airports" entries share the iata code
  // with the specific airport (e.g. LAX appears twice for Los Angeles).
  const seen = new Set()
  return (json.data || [])
    .flatMap(group => group.airports || [])
    .filter(ap => {
      const key = `${ap.iata}-${ap.icao || 'all'}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
};

// API endpoints - all go through Cloudflare Worker proxy
export const API_ENDPOINTS = {
  places: `${CLOUDFLARE_WORKER_URL}/api/data/places`,
  hotels: `${CLOUDFLARE_WORKER_URL}/api/data/hotels`,
  hotel: `${CLOUDFLARE_WORKER_URL}/api/data/hotel`,
  rates: `${CLOUDFLARE_WORKER_URL}/api/hotels/rates`,
  reviews: `${CLOUDFLARE_WORKER_URL}/api/data/reviews`,
  hotelAsk: `${CLOUDFLARE_WORKER_URL}/api/data/hotel/ask`,
  prebook: `${CLOUDFLARE_WORKER_URL}/api/rates/prebook`,
  book: `${CLOUDFLARE_WORKER_URL}/api/rates/book`,
};

const ENGLISH_DATA_LANGUAGE = 'en';

const withEnglishLanguage = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}language=${ENGLISH_DATA_LANGUAGE}`;
};

// Vacation rental hotel type IDs for data endpoint filtering
export const VR_TYPE_IDS = [201, 213, 219, 220, 222, 228, 229, 230, 250, 207, 216];

// Dynamic margin calculation based on login status
// - Guest: 10%
// - Logged in: 8%
// - User-specific: customMargin field on Firestore user doc (overrides logged-in margin)
export const calculateMargin = (isLoggedIn, checkin, checkout, userMargin) => {
  // If a user-specific margin is set (including 0), use it
  const margin = (isLoggedIn && typeof userMargin === 'number') ? userMargin : (isLoggedIn ? 8 : 10);
  
  console.log(`[Margin] isLoggedIn: ${isLoggedIn}, userMargin: ${userMargin}, margin: ${margin}%`);
  return margin;
};

// Helper to make authenticated API calls to Cloudflare Worker
// Pass timeout = 0 to disable timeout (wait indefinitely - used for booking confirmation)
export const fetchWithAuth = async (url, options = {}, timeout = 30000, maxRetries = 2) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Voyabay-Auth': VOYABAY_API_TOKEN,
    'X-Voyabay-Logged-In': auth.currentUser ? '1' : '0',
    ...(options.headers || {}),
  };
  
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const cacheBuster = `_t=${Date.now()}`;
    const urlWithCacheBuster = url.includes('?') ? `${url}&${cacheBuster}` : `${url}?${cacheBuster}`;
    
    // Create abort controller for timeout (skip if timeout is 0 = no timeout)
    const controller = new AbortController();
    let timeoutId = null;
    if (timeout > 0) {
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }
    
    try {
      const response = await fetch(urlWithCacheBuster, {
        ...options,
        headers,
        signal: timeout > 0 ? controller.signal : undefined,
      });
      if (timeoutId) clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      lastError = error;
      
      console.warn(`[API] Request failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error.message);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      
      if (attempt < maxRetries) {
        console.log(`[API] Retrying in ${(attempt + 1) * 500}ms...`);
        await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 500));
      }
    }
  }
  
  throw lastError || new Error('Request failed after retries');
};

// Get place details by placeId (for getting coordinates)
export const getPlaceDetails = async (placeId) => {
  if (!placeId) throw new Error('PlaceId required');
  
  const response = await fetchWithAuth(`${API_ENDPOINTS.places}/${encodeURIComponent(placeId)}`);
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  
  const json = await response.json();
  return json.data || json;
};

// Get hotel data by IDs (for facilities, stars, etc.)
export const getHotelsByIds = async (hotelIds) => {
  if (!hotelIds || hotelIds.length === 0) return { data: [] };
  
  const idsParam = hotelIds.join(',');
  const response = await fetchWithAuth(withEnglishLanguage(`${API_ENDPOINTS.hotels}?hotelIds=${idsParam}`));

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get hotel list by location (placeId or coordinates)
export const getHotelListByLocation = async ({ placeId, latitude, longitude, radius = 20, limit = 1000 }) => {
  let url = withEnglishLanguage(`${API_ENDPOINTS.hotels}?limit=${limit}`);
  
  if (placeId) {
    url += `&placeId=${placeId}`;
  } else if (latitude && longitude) {
    url += `&latitude=${latitude}&longitude=${longitude}&radius=${radius}`;
  } else {
    throw new Error('Either placeId or coordinates required');
  }
  
  console.log('[API] getHotelListByLocation - URL:', url);
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  const data = await response.json();
  console.log('[API] getHotelListByLocation - Got', data.data?.length || 0, 'hotels');
  return data;
};

// Get hotel rates by hotelIds - for search results page
export const getHotelRatesByIds = async ({ hotelIds, checkin, checkout, adults, rooms, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, userMargin }) => {
  if (!hotelIds || hotelIds.length === 0) return { data: [] };
  
  let occupancies;
  
  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({ adults: adultsPerRoom, children: [] });
    }
  }
  
  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  
  const requestBody = {
    hotelIds,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    maxRatesPerHotel: 1,
    roomMapping: true,
    includeHotelData: true,
    margin,
  };

  console.log('[API] getHotelRatesByIds - Checking rates for', hotelIds.length, 'hotels, margin:', margin);

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get hotel rates from LiteAPI - for search results page
export const getHotelRates = async ({ placeId, checkin, checkout, adults, rooms, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, vacationRentals = false, userMargin }) => {
  let occupancies;
  
  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({
        adults: adultsPerRoom,
        children: []
      });
    }
  }
  
  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  console.log('[API] getHotelRates - isLoggedIn:', isLoggedIn, 'margin:', margin, 'occupancies:', occupancies, 'vacationRentals:', vacationRentals);
  
  const requestBody = {
    placeId,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    maxRatesPerHotel: 1,
    roomMapping: true,
    includeHotelData: true,
    limit: vacationRentals ? 5000 : 2000,
    margin,
  };

  // Add vacation rental hotel types filter
  if (vacationRentals) {
    requestBody.hotelTypeIds = VR_TYPE_IDS;
  }

  console.log('[API] getHotelRates - FULL REQUEST:', JSON.stringify(requestBody, null, 2));

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get single hotel details (photos, description, facilities, etc.)
const getServerHotelDetails = (hotelId) => {
  if (typeof document === 'undefined') return null;

  const element = document.getElementById('voyabay-server-hotel-data');
  if (!element?.textContent) return null;

  try {
    const bootstrap = JSON.parse(element.textContent);
    return String(bootstrap.hotelId) === String(hotelId) && bootstrap.data
      ? { data: bootstrap.data }
      : null;
  } catch (error) {
    console.warn('[API] Ignoring invalid server hotel bootstrap data:', error);
    return null;
  }
};

export const getHotelDetails = async (hotelId) => {
  if (!hotelId) {
    throw new Error('Hotel ID is required');
  }

  const serverDetails = getServerHotelDetails(hotelId);
  if (serverDetails) {
    console.log('[API] getHotelDetails - using server bootstrap data for hotel:', hotelId);
    return serverDetails;
  }
  
  console.log('[API] getHotelDetails - fetching hotel:', hotelId);
  const response = await fetchWithAuth(withEnglishLanguage(`${API_ENDPOINTS.hotel}?hotelId=${hotelId}`));

  console.log('[API] getHotelDetails - response status:', response.status);
  
  if (!response.ok) {
    console.error('[API] getHotelDetails - API error:', response.status, response.statusText);
    throw new Error(`API Error: ${response.status}`);
  }

  try {
    const text = await response.text();
    console.log('[API] getHotelDetails - raw response length:', text.length);
    
    if (!text || text.trim() === '') {
      console.error('[API] getHotelDetails - empty response');
      return { data: null };
    }
    
    const json = JSON.parse(text);
    console.log('[API] getHotelDetails - parsed successfully, has data:', !!json.data);
    return json;
  } catch (e) {
    console.error('[API] getHotelDetails - JSON parse error:', e);
    throw new Error('Invalid response from server');
  }
};

// Get hotel rates by hotelId (for hotel details page - returns all room options)
export const getHotelRatesById = async ({ hotelIds, checkin, checkout, adults, rooms, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, userMargin }) => {
  let occupancies;
  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({ adults: adultsPerRoom, children: [] });
    }
  }
  
  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  console.log('[API] getHotelRatesById - isLoggedIn:', isLoggedIn, 'margin:', margin);
  
  const requestBody = {
    hotelIds,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    roomMapping: true,
    margin,
  };

  console.log('[API] getHotelRatesById - FULL REQUEST:', JSON.stringify(requestBody, null, 2));

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get hotel data from hotel name (for hotel-specific searches)
export const getHotelFromPlaceId = async (placeId, countryCode = 'US', hotelName = null) => {
  let url;
  
  if (hotelName) {
    console.log('[getHotelFromPlaceId] Searching by hotelName:', hotelName, 'countryCode:', countryCode);
    url = withEnglishLanguage(`${API_ENDPOINTS.hotels}?hotelName=${encodeURIComponent(hotelName)}&countryCode=${countryCode}&limit=1`);
  } else {
    console.log('[getHotelFromPlaceId] Searching by placeId:', placeId);
    url = withEnglishLanguage(`${API_ENDPOINTS.hotels}?placeId=${placeId}&countryCode=${countryCode}`);
  }
  
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  const data = await response.json();
  console.log('[getHotelFromPlaceId] API response:', data);
  
  if (data.data && data.data.length > 0) {
    const hotel = data.data[0];
    console.log('[getHotelFromPlaceId] Returning hotel:', hotel.id, hotel.name);
    return {
      id: hotel.id,
      name: hotel.name,
      latitude: hotel.latitude,
      longitude: hotel.longitude
    };
  }
  
  console.log('[getHotelFromPlaceId] No hotel found');
  return null;
};

// Get vacation rental rates — single call with hotelTypeIds filter (same as hotel search)
export const getVacationRentalRates = async ({ latitude, longitude, placeId, checkin, checkout, adults, rooms, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, userMargin }) => {
  let occupancies;

  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({ adults: adultsPerRoom, children: [] });
    }
  }

  // If we only have placeId, convert to coords first
  let coords = { lat: latitude, lng: longitude };
  if (!coords.lat && placeId) {
    console.log('[VR] Converting placeId to coords:', placeId);
    const placeData = await getPlaceDetails(placeId);
    coords.lat = placeData?.location?.latitude;
    coords.lng = placeData?.location?.longitude;
    if (!coords.lat || !coords.lng) {
      console.error('[VR] Could not get coords from placeId');
      return { data: [], hotels: [] };
    }
  }

  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  console.log('[VR] Single-call VR search — coords:', coords.lat, coords.lng, 'margin:', margin);

  const requestBody = {
    latitude: coords.lat,
    longitude: coords.lng,
    radius: 30000,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    maxRatesPerHotel: 1,
    roomMapping: true,
    includeHotelData: true,
    hotelTypeIds: VR_TYPE_IDS,
    limit: 5000,
    margin,
  };

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  const ratesData = await response.json();

  // Normalize rate entries — API can return slightly different shapes
  const rateEntries = (
    Array.isArray(ratesData?.data) ? ratesData.data
      : Array.isArray(ratesData?.data?.hotels) ? ratesData.data.hotels
      : Array.isArray(ratesData?.ratesData?.hotels) ? ratesData.ratesData.hotels
      : []
  );

  const hasAnyRates = (entry) => {
    const roomTypes = Array.isArray(entry?.roomTypes) ? entry.roomTypes : [];
    if (roomTypes.length > 0) return true;
    const rates = Array.isArray(entry?.rates) ? entry.rates : [];
    return rates.length > 0;
  };

  const normalizedRateEntries = rateEntries
    .filter(hasAnyRates)
    .map((entry) => {
      const normalized = {
        ...entry,
        hotelId: entry.hotelId || entry.id,
      };

      // Some payloads return rates directly on the hotel object.
      // Search pipeline expects roomTypes[0].rates[0].
      if ((!normalized.roomTypes || normalized.roomTypes.length === 0) && Array.isArray(normalized.rates) && normalized.rates.length > 0) {
        normalized.roomTypes = [{
          rates: normalized.rates,
          offerRetailRate: normalized.offerRetailRate,
          suggestedSellingPrice: normalized.suggestedSellingPrice,
          offerId: normalized.offerId,
        }];
      }

      return normalized;
    });

  if (normalizedRateEntries.length === 0) {
    console.log('[VR] No VR hotels returned rates');
    return { data: [], hotels: [], guestLevel: ratesData.guestLevel };
  }

  console.log('[VR] Found', normalizedRateEntries.length, 'VR hotels with rates');

  const hotelIdsWithRates = new Set(normalizedRateEntries.map(h => String(h.hotelId || h.id)));
  const hotelsMeta = (
    Array.isArray(ratesData?.hotels) ? ratesData.hotels
      : Array.isArray(ratesData?.ratesData?.hotels) ? ratesData.ratesData.hotels
      : Array.isArray(ratesData?.data?.properties) ? ratesData.data.properties
      : []
  );

  const filteredHotels = hotelsMeta.filter(h => hotelIdsWithRates.has(String(h.id || h.hotelId)));

  return {
    data: normalizedRateEntries,
    hotels: filteredHotels,
    guestLevel: ratesData.guestLevel,
  };
};

// Single-call nearby VR deals for the details page carousel (fast path)
// Skips the two-step VR search in favour of one coords+hotelTypeIds rates call.
export const getNearbyVRDealsQuick = async ({ latitude, longitude, checkin, checkout, adults = 2, rooms = 1, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, userMargin }) => {
  let occupancies;
  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({ adults: adultsPerRoom, children: [] });
    }
  }

  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);

  const requestBody = {
    latitude,
    longitude,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    maxRatesPerHotel: 1,
    roomMapping: true,
    includeHotelData: true,
    hotelTypeIds: VR_TYPE_IDS,
    limit: 20,
    radius: 30000,
    minReviewRating: 6,
    margin,
  };

  console.log('[API] getNearbyVRDealsQuick - single-call VR nearby, limit:20, radius:30000, minReviewRating:6');

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) throw new Error(`API Error: ${response.status}`);

  const ratesData = await response.json();

  const data = (
    Array.isArray(ratesData?.data) ? ratesData.data
      : Array.isArray(ratesData?.data?.hotels) ? ratesData.data.hotels
      : []
  );
  let hotels = (
    Array.isArray(ratesData?.hotels) ? ratesData.hotels
      : Array.isArray(ratesData?.ratesData?.hotels) ? ratesData.ratesData.hotels
      : []
  );

  // If hotels came back empty but rate entries have embedded hotel data, extract them
  if (hotels.length === 0 && data.length > 0) {
    hotels = data
      .filter(e => e.hotel || e.hotelData)
      .map(e => ({ ...(e.hotel || e.hotelData), id: e.hotelId || e.id }));
  }

  return { data, hotels };
};

// Get hotel rates by coordinates (for hotel searches)
export const getHotelRatesByCoords = async ({ latitude, longitude, checkin, checkout, adults, rooms, occupancies: providedOccupancies, currency = 'USD', isLoggedIn = false, vacationRentals = false, userMargin }) => {
  let occupancies;
  
  if (providedOccupancies && providedOccupancies.length > 0) {
    occupancies = providedOccupancies;
  } else {
    occupancies = [];
    const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
    
    for (let i = 0; i < parseInt(rooms); i++) {
      occupancies.push({
        adults: adultsPerRoom,
        children: []
      });
    }
  }
  
  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  console.log('[API] getHotelRatesByCoords - isLoggedIn:', isLoggedIn, 'margin:', margin, 'occupancies:', occupancies, 'vacationRentals:', vacationRentals);
  
  const requestBody = {
    latitude,
    longitude,
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    maxRatesPerHotel: 1,
    roomMapping: true,
    includeHotelData: true,
    limit: vacationRentals ? 5000 : 2000,
    margin
  };

  // Add vacation rental hotel types filter
  if (vacationRentals) {
    requestBody.hotelTypeIds = VR_TYPE_IDS;
  }

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Ask AI about a hotel
export const askHotelAI = async (hotelId, query) => {
  const params = new URLSearchParams({
    hotelId,
    query,
    allowWebSearch: 'true'
  });
  
  const response = await fetchWithAuth(`${API_ENDPOINTS.hotelAsk}?${params}`);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get hotel rates for checkout
export const getCheckoutRates = async ({ hotelId, checkin, checkout, adults, rooms, currency = 'USD', isLoggedIn = false, userMargin }) => {
  const occupancies = [];
  const adultsPerRoom = Math.ceil(parseInt(adults) / parseInt(rooms));
  
  for (let i = 0; i < parseInt(rooms); i++) {
    occupancies.push({
      adults: adultsPerRoom,
      children: []
    });
  }
  
  const margin = calculateMargin(isLoggedIn, checkin, checkout, userMargin);
  
  const requestBody = {
    hotelIds: [hotelId],
    checkin,
    checkout,
    occupancies,
    currency,
    guestNationality: 'US',
    roomMapping: true,
    margin
  };

  const response = await fetchWithAuth(API_ENDPOINTS.rates, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Get hotel reviews
export const getHotelReviews = async (hotelId, limit = 5000) => {
  const params = new URLSearchParams({
    hotelId,
    limit: String(limit),
  });
  
  const response = await fetchWithAuth(`${API_ENDPOINTS.reviews}?${params}`);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
};

// Prebook - Create checkout session
export const prebookRate = async ({ offerId, voucherCode = null }) => {
  if (!offerId) {
    throw new Error('Offer ID is required for prebook');
  }

  const requestBody = {
    offerId,
    usePaymentSdk: true,
  };

  if (voucherCode && voucherCode.trim()) {
    requestBody.voucherCode = voucherCode.trim();
  }

  console.log('[API] prebookRate - request:', requestBody);

  const response = await fetchWithAuth(API_ENDPOINTS.prebook, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  }, 0, 0);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[API] prebookRate - error:', response.status, errorText);
    throw new Error(`Prebook failed: ${response.status}`);
  }

  const result = await response.json();
  console.log('[API] prebookRate - response:', result);
  
  return result;
};

// Book - Finalize the booking after payment
export const bookRate = async ({ prebookId, transactionId, holder, guests }) => {
  if (!prebookId || !transactionId) {
    throw new Error('Prebook ID and Transaction ID are required');
  }

  const requestBody = {
    prebookId,
    holder: {
      firstName: holder.firstName,
      lastName: holder.lastName,
      email: holder.email,
    },
    payment: {
      method: 'TRANSACTION_ID',
      transactionId,
    },
    guests: guests || [{
      occupancyNumber: 1,
      remarks: '',
      firstName: holder.firstName,
      lastName: holder.lastName,
      email: holder.email,
      phone: holder.phone || '',
    }],
  };

  console.log('[API] bookRate - request:', requestBody);

  // No timeout (0) for booking - payment verification popups (Capital One, etc.) can delay the response
  // We never want to abort a booking confirmation call
  const response = await fetchWithAuth(API_ENDPOINTS.book, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  }, 0, 3);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[API] bookRate - error:', response.status, errorText);
    throw new Error(`Booking failed: ${response.status}`);
  }

  const result = await response.json();
  console.log('[API] bookRate - response:', result);
  
  return result;
};

// Retrieve a booking by its booking ID
export const getBooking = async (bookingId) => {
  if (!bookingId) {
    throw new Error('Booking ID is required');
  }

  console.log('[API] getBooking - fetching:', bookingId);

  const response = await fetchWithAuth(`${CLOUDFLARE_WORKER_URL}/api/bookings/${bookingId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[API] getBooking - error:', response.status, errorText);
    throw new Error(`Failed to retrieve booking: ${response.status}`);
  }

  const result = await response.json();
  console.log('[API] getBooking - response:', result);
  return result;
};
